/**
 * Draw a watched area straight on the mission map.
 *
 * COP creates geofences with mapbox-gl-draw; the mission map already has a
 * click-capture seam for tools (waypoint picking), so this reuses it: each
 * click adds a corner, Finish names it, and the save creates a global
 * geofence and links it to the mission in one gesture. The polygon previews
 * in gold so a pending area cannot be mistaken for a watched one.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Layer, Source } from 'react-map-gl';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { geofenceService } from '../../services/geofenceService';
import { Mission, replaceMissionGeofences } from '../../services/missionApi';
import { Action } from './missionLiveParts';

const GOLD = '#f0b429';

export interface AreaDraw {
  drawing: boolean;
  vertices: [number, number][];
  saving: boolean;
  naming: boolean;
  start: () => void;
  cancel: () => void;
  addVertex: (lng: number, lat: number) => void;
  undoVertex: () => void;
  finish: () => void;
  dismissNaming: () => void;
  save: (name: string) => Promise<void>;
}

export function useAreaDraw(
  mission: Mission | null,
  opts: { onSaved: () => void; onError: (message: string | null) => void },
): AreaDraw {
  const [drawing, setDrawing] = useState(false);
  const [vertices, setVertices] = useState<[number, number][]>([]);
  const [naming, setNaming] = useState(false);
  const [saving, setSaving] = useState(false);
  // The mission and callbacks live in refs so addVertex handed to the map
  // does not go stale between renders.
  const missionRef = useRef(mission);
  missionRef.current = mission;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const start = useCallback(() => {
    setVertices([]);
    setNaming(false);
    setDrawing(true);
  }, []);

  const cancel = useCallback(() => {
    setDrawing(false);
    setNaming(false);
    setVertices([]);
  }, []);

  const addVertex = useCallback((lng: number, lat: number) => {
    setVertices((current) => [...current, [lng, lat]]);
  }, []);

  const undoVertex = useCallback(() => {
    setVertices((current) => current.slice(0, -1));
  }, []);

  const finish = useCallback(() => {
    setNaming(true);
  }, []);

  const dismissNaming = useCallback(() => {
    setNaming(false);
  }, []);

  const save = useCallback(async (name: string) => {
    const current = missionRef.current;
    if (!current || vertices.length < 3) return;
    setSaving(true);
    optsRef.current.onError(null);
    try {
      const ring = [...vertices, vertices[0]];
      const created = await geofenceService.createGeofence({
        name,
        description: `Drawn on the mission map for "${current.name}"`,
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      await replaceMissionGeofences(current.id, [
        ...current.geofences.map((g) => g.geofence_id),
        created.id,
      ]);
      setDrawing(false);
      setNaming(false);
      setVertices([]);
      optsRef.current.onSaved();
    } catch (e: any) {
      optsRef.current.onError(e?.message || 'Could not save the area');
    } finally {
      setSaving(false);
    }
  }, [vertices]);

  return { drawing, vertices, saving, naming, start, cancel, addVertex, undoVertex, finish, dismissNaming, save };
}

/** Pending polygon on the map: gold dashed outline, corner dots, soft fill. */
export const AreaDrawPreview: React.FC<{ vertices: [number, number][] }> = ({ vertices }) => {
  if (vertices.length === 0) return null;
  const features: GeoJSON.Feature[] = [
    {
      type: 'Feature',
      properties: { part: 'points' },
      geometry: { type: 'MultiPoint', coordinates: vertices },
    },
  ];
  if (vertices.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { part: 'line' },
      geometry: { type: 'LineString', coordinates: [...vertices, vertices[0]] },
    });
  }
  if (vertices.length >= 3) {
    features.push({
      type: 'Feature',
      properties: { part: 'fill' },
      geometry: { type: 'Polygon', coordinates: [[...vertices, vertices[0]]] },
    });
  }
  return (
    <Source
      id="mission-area-draw"
      type="geojson"
      data={{ type: 'FeatureCollection', features }}
    >
      <Layer
        id="mission-area-draw-fill"
        type="fill"
        filter={['==', ['get', 'part'], 'fill']}
        paint={{ 'fill-color': GOLD, 'fill-opacity': 0.12 }}
      />
      <Layer
        id="mission-area-draw-line"
        type="line"
        filter={['==', ['get', 'part'], 'line']}
        paint={{ 'line-color': GOLD, 'line-width': 2, 'line-dasharray': [2, 1.5] }}
      />
      <Layer
        id="mission-area-draw-points"
        type="circle"
        filter={['==', ['get', 'part'], 'points']}
        paint={{
          'circle-radius': 4,
          'circle-color': GOLD,
          'circle-stroke-color': '#16181c',
          'circle-stroke-width': 1.5,
        }}
      />
    </Source>
  );
};

/** Floating controls while drawing, plus the naming dialog. */
export const AreaDrawOverlay: React.FC<{ draw: AreaDraw }> = ({ draw }) => {
  const [name, setName] = useState('');
  if (!draw.drawing) return null;
  const corners = draw.vertices.length;
  return (
    <>
      <Box
        sx={{
          // Top centre: the intel page pins a time slider along the bottom
          // and both pages keep collapse handles on the side edges.
          position: 'absolute',
          left: '50%',
          top: 12,
          transform: 'translateX(-50%)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: 'rgba(15,18,23,0.92)',
          border: `1px solid ${GOLD}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>
          Drawing area
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          {corners === 0
            ? 'Click the map to add corners'
            : `${corners} corner${corners === 1 ? '' : 's'}${corners < 3 ? ` · ${3 - corners} more to close` : ''}`}
        </Typography>
        {corners > 0 && (
          <Action onClick={draw.undoVertex}>Undo</Action>
        )}
        <Action filled disabled={corners < 3 || draw.saving} onClick={draw.finish}>
          Finish
        </Action>
        <Action danger onClick={draw.cancel}>Cancel</Action>
      </Box>

      <Dialog open={draw.naming} onClose={draw.dismissNaming} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
            Name this area
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            The area is saved as a geofence and this mission starts watching it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Area name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim() && !draw.saving) draw.save(name.trim());
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={draw.dismissNaming}>Back</Button>
            <Button
              size="small"
              variant="contained"
              disabled={!name.trim() || draw.saving}
              onClick={() => draw.save(name.trim())}
            >
              {draw.saving ? 'Saving…' : 'Watch this area'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
